import { useEffect, useState, useRef } from "react";
import { Link } from "react-router";
import { ArrowLeft, ArrowRight, Mail, Globe, ExternalLink } from "lucide-react";
import { useAppContext } from "@/providers";
import { Logo } from "@/components/layout/logo";
import { termsAndConditionsAr, termsAndConditionsEn, TermsSection } from "@/data/terms-and-conditions";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { MetaTags } from "@/components/seo/meta-tags";
import { cn } from "@/lib/utils";

export default function TermsAndConditionsPage() {
  const { locale, setLocale } = useAppContext();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const isRTL = locale === "ar";

  const data = isRTL ? termsAndConditionsAr : termsAndConditionsEn;
  const pageTitle = `${data.title} | NCircle`;
  useDocumentTitle(pageTitle);

  const handleLanguageChange = (newLocale: string) => {
    setLocale(newLocale as "en" | "ar");
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(event.target as Node)
      ) {
        setIsLangMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderSection = (section: TermsSection, index: number) => (
    <article
      key={index}
      className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-sm transition-all hover:border-border/80"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-2.5 h-2.5 rounded-full bg-primary shrink-0" />
        <h2 className="text-xl md:text-2xl font-bold text-foreground tracking-tight">
          {section.title}
        </h2>
      </div>

      {section.content && (
        <p className="text-foreground-secondary leading-relaxed text-base mb-4">
          {section.content}
        </p>
      )}

      {section.items && (
        <ul className={cn("space-y-2.5 mb-4 text-foreground-secondary", isRTL ? "pr-5" : "pl-5")}>
          {section.items.map((item, i) => (
            <li key={i} className="leading-relaxed text-base list-disc marker:text-primary">
              {item}
            </li>
          ))}
        </ul>
      )}

      {section.subsections && (
        <div className="space-y-4 mt-5 pt-4 border-t border-border/50">
          {section.subsections.map((sub, i) => (
            <div key={i} className="p-4 md:p-5 bg-muted/40 rounded-xl border border-border/40">
              <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-secondary shrink-0" />
                {sub.title}
              </h3>
              {sub.items && (
                <ul className={cn("space-y-2 text-foreground-secondary text-sm md:text-base", isRTL ? "pr-5" : "pl-5")}>
                  {sub.items.map((item, j) => (
                    <li key={j} className="leading-relaxed list-disc marker:text-secondary">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {section.contactInfo && (
        <div className="mt-6 p-5 bg-muted/30 rounded-xl border border-border/60">
          <p className="font-semibold text-foreground mb-3 text-base">
            {isRTL ? "بيانات التواصل:" : "Contact Information:"}
          </p>
          <div className="space-y-2.5 text-foreground-secondary text-sm md:text-base">
            <a
              href={`mailto:${section.contactInfo.email}`}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark hover:underline font-medium"
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span>{section.contactInfo.email}</span>
            </a>
            <div>
              <a
                href={`https://${section.contactInfo.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-primary-dark hover:underline font-medium"
              >
                <Globe className="w-4 h-4 shrink-0" />
                <span>{section.contactInfo.website}</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            </div>
          </div>
        </div>
      )}
    </article>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans">
      <MetaTags
        description={data.introduction}
        ogTitle={pageTitle}
        ogDescription={data.introduction}
        ogImage="/og-image.png"
        ogUrl="https://partner.ncircle.ai/terms-and-conditions"
        ogType="website"
      />

      {/* Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Logo size="md" />

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langMenuRef}>
              <button
                type="button"
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="text-sm font-medium text-foreground-secondary hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
              >
                {locale === "ar" ? "EN" : "عربي"}
              </button>
              {isLangMenuOpen && (
                <div className="absolute top-full mt-2 end-0 w-32 bg-card rounded-lg shadow-lg border border-border py-1 z-50 animate-slide-in-top">
                  <button
                    type="button"
                    onClick={() => handleLanguageChange("ar")}
                    className={cn(
                      "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                      locale === "ar"
                        ? "text-primary font-bold"
                        : "text-foreground-secondary"
                    )}
                  >
                    العربية
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLanguageChange("en")}
                    className={cn(
                      "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                      locale === "en"
                        ? "text-primary font-bold"
                        : "text-foreground-secondary"
                    )}
                  >
                    English
                  </button>
                </div>
              )}
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground-secondary hover:text-foreground px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              <span>{isRTL ? "العودة للرئيسية" : "Back to Home"}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          {/* Breadcrumb & Badge */}
          <div className="mb-6 flex items-center gap-2 text-sm text-foreground-secondary">
            <Link to="/" className="hover:text-foreground transition-colors">
              {isRTL ? "الرئيسية" : "Home"}
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">
              {isRTL ? "الشروط والأحكام" : "Terms and Conditions"}
            </span>
          </div>

          <div className="mb-10 pb-8 border-b border-border">
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
              {data.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-secondary">
              {isRTL ? "آخر تحديث:" : "Last updated:"}{" "}
              <span className="font-semibold text-foreground">{data.lastUpdated}</span>
            </p>
          </div>

          {/* Introduction Card */}
          <div className="mb-10 p-6 md:p-8 bg-muted/30 border border-border rounded-2xl">
            <p className="text-foreground-secondary leading-relaxed text-base md:text-lg">
              {data.introduction}
            </p>
          </div>

          {/* Sections List */}
          <div className="space-y-6">
            {data.sections.map((section, idx) => renderSection(section, idx))}
          </div>

          {/* Bottom Back Button */}
          <div className="mt-12 pt-8 border-t border-border flex justify-between items-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-pill hover:bg-primary-dark transition-all shadow-sm active:scale-95 text-sm"
            >
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              <span>{isRTL ? "العودة للرئيسية" : "Return to Home"}</span>
            </Link>

            <span className="text-xs text-foreground-secondary">
              © {new Date().getFullYear()} NCircle. {isRTL ? "جميع الحقوق محفوظة." : "All rights reserved."}
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-neutral-900 text-neutral-100 py-8 border-t border-white/10 mt-12 text-center text-xs text-neutral-400">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} NCircle · {isRTL ? "جميع الحقوق محفوظة" : "All rights reserved"}</p>
          <div className="flex gap-6">
            <Link to="/terms-and-conditions" className="text-white hover:underline">
              {isRTL ? "الشروط والأحكام" : "Terms & Conditions"}
            </Link>
            <Link to="/privacy-policy" className="text-neutral-400 hover:text-white">
              {isRTL ? "سياسة الخصوصية" : "Privacy Policy"}
            </Link>
            <Link to="/" className="text-neutral-400 hover:text-white">
              {isRTL ? "الرئيسية" : "Home"}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
